import { Checkbox } from '@/components/atoms/Checkbox';
import { StatusChip } from '@/components/atoms/StatusChip';
import { Typography } from '@/components/atoms/Typography';
import { ChevronRightIcon, DirhamIcon } from '@/components/atoms/icons';
import PaymentDetailsDialog from '@/components/organisms/PaymentDetailsDialog';
import { useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';
import { router } from 'expo-router';
import React, { useState } from 'react';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';

export interface PaymentItemProps {
    id: string;
    title: string;
    amount: number;
    dueDate: string;
    status: 'paid' | 'missed' | 'due-soon' | 'in-process' | 'future';
    type?: 'property' | 'community';
    isSelected?: boolean;
    isDisabled?: boolean;
    showPropertyName?: boolean;
    propertyName?: string;
    /**
     * Profile unit `isOnlinePaymentEnabled` for this payment's sales order.
     * False hides the selection checkbox. Defaults to true for callers outside
     * the owned-unit payment list.
     */
    onlinePaymentEnabled?: boolean;
    onSelect?: (paymentId: string) => void;
    onPress?: () => void;
    /**
     * Visual variant.
     * - 'default': mobile / card layout
     * - 'large': web table-style row layout (Property payments web)
     */
    variant?: 'default' | 'large';
}

export function PaymentItemMobile({
    id,
    title,
    amount,
    dueDate,
    status,
    type = 'property',
    isSelected = false,
    isDisabled = false,
    showPropertyName = true,
    propertyName = 'Property name',
    onlinePaymentEnabled = true,
    onSelect,
    onPress,
    variant = 'default',
}: PaymentItemProps) {
    const colors = useUIColors();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    const [isDialogVisible, setIsDialogVisible] = useState(false);

    const handleCheckboxPress = () => {
        if (isDisabled) return;
        if (onSelect) {
            trackDynatraceEvent({
                p_screen: 'Property Payments',
                p_actionName: isSelected ? 'Payment Item Deselected' : 'Payment Item Selected',
                p_sectionName: 'Payment List',
                eventType: 'action',
                paymentId: id,
                paymentAmount: amount,
                paymentStatus: status,
                propertyName: propertyName,
            });
            onSelect(id);
        }
    };

    const handlePaymentPress = () => {
        if (isDisabled) return;
        
        trackDynatraceEvent({
            p_screen: 'Property Payments',
            p_actionName: 'Payment Details Viewed',
            p_sectionName: 'Payment List',
            eventType: 'view',
            paymentId: id,
            paymentAmount: amount,
            paymentStatus: status,
            propertyName: propertyName,
        });
        if (onPress) {
            onPress();
            return;
        }

        // On web, open payment details in a dialog instead of changing route
        if (Platform.OS === 'web') {
            setIsDialogVisible(true);
            return;
        }

        // Default navigation to payment details screen (native)
        router.push(`/(auth)/payment-details?id=${id}` as any);
    };

    const getStatusChipStatus = (): 'paid' | 'missed' | 'due-soon' | 'in-process' => {
        switch (status) {
            case 'missed':
                return 'missed';
            case 'due-soon':
                return 'due-soon';
            case 'in-process':
                return 'in-process';
            case 'paid':
                return 'paid';
            default:
                return 'in-process';
        }
    };

    const isLargeWebVariant = variant === 'large' && Platform.OS === 'web';

    const shouldShowCheckbox = () => {
        if (!onlinePaymentEnabled) return false;
        // Show checkbox for actionable payments (missed, due-soon, and future)
        return status === 'missed' || status === 'due-soon' || status === 'future';
    };

    const showBankPayNote = status === 'missed' && !onlinePaymentEnabled;

    const shouldShowStatusChip = () => {
        // Don't show status chip for future payments
        return status !== 'future';
    };

    const getContainerStyle = () => {
        // Selected state - use backgroundLevel2 (#f5f5f5) as per Figma
        // Border disappears when selected
        if (isSelected) {
            return {
                backgroundColor: colors.backgroundLevel2, // #f5f5f5
                borderColor: 'transparent', // Border disappears when selected
                borderWidth: 0,
            };
        }

        // Error state (missed payments) - red border
        if (status === 'missed') {
            return {
                backgroundColor: colors.backgroundLevel1, // #ffffff
                borderColor: colors.outlineError, // #ef5250
            };
        }

        // Default state
        return {
            backgroundColor: colors.backgroundLevel1, // #ffffff
            borderColor: colors.outlineInactive, // #cccccc
        };
    };

    const getLeftBorderStyle = () => {
        // No left border needed - removed as per Figma
        return null;
    };

    return (
        <View
            style={[
                styles.container,
                { flexDirection: isRTL ? 'row-reverse' : 'row' },
                isLargeWebVariant && styles.containerLarge,
            ]}
        >
            {/* Checkbox Area - Only show for actionable payments */}
            {shouldShowCheckbox() && (
                <TouchableOpacity
                    style={styles.checkboxArea}
                    onPress={handleCheckboxPress}
                    disabled={isDisabled}
                    activeOpacity={isDisabled ? 1 : 0.7}
                >
                    <Checkbox
                        isSelected={isSelected}
                        isDisabled={isDisabled}
                        onPress={handleCheckboxPress}
                        size={19}
                    />
                </TouchableOpacity>
            )}

            {/* Payment Content */}
            <TouchableOpacity
                style={[
                    styles.paymentContent,
                    getContainerStyle(),
                    isLargeWebVariant && styles.paymentContentLarge,
                    {
                        opacity: isDisabled ? 0.6 : 1,
                    },
                ]}
                onPress={handlePaymentPress}
                disabled={isDisabled}
                activeOpacity={isDisabled ? 1 : 0.7}
            >
                {/* Left border for error states */}
                {getLeftBorderStyle() && (
                    <View style={getLeftBorderStyle()} />
                )}

                <View style={[styles.content, isLargeWebVariant && styles.contentLarge]}>
                    {/* Property Name */}
                    {showPropertyName && (
                        <Typography
                            variant={isLargeWebVariant ? 'bodyLarge' : 'footnote'}
                            color="secondary"
                            style={[styles.propertyName, isLargeWebVariant && styles.propertyNameLarge]}
                        >
                            {propertyName}
                        </Typography>
                    )}

                    {/* Payment Header */}
                    <View style={[styles.paymentHeader, isLargeWebVariant && styles.paymentHeaderLarge]}>
                        <Typography
                            variant={isLargeWebVariant ? 'headline' : 'headline'}
                            color="primary"
                            style={[styles.paymentTitle, isLargeWebVariant && styles.paymentTitleLarge]}
                            numberOfLines={1}
                        >
                            {title}
                        </Typography>
                        <View style={[styles.paymentAmountContainer, isLargeWebVariant && styles.paymentAmountContainerLarge]}>
                            <View style={[styles.paymentAmount, { direction: 'ltr' }]}>
                                {/* UAE Dirham Symbol - always on left of number */}
                                <DirhamIcon size={isLargeWebVariant ? 18 : 14} color={colors.fontPrimary} />
                                <Typography
                                    variant={isLargeWebVariant ? 'title3' : 'headline'}
                                    color="primary"
                                    style={[styles.amountText, isLargeWebVariant && styles.amountTextLarge]}
                                >
                                    {amount.toLocaleString()}
                                </Typography>
                            </View>
                            <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                                <ChevronRightIcon size={isLargeWebVariant ? 20 : 16} color={colors.fontPrimary} />
                            </View>
                        </View>
                    </View>

                    {/* Payment Footer */}
                    <View style={[styles.paymentFooter, isLargeWebVariant && styles.paymentFooterLarge]}>
                        <Typography
                            variant={isLargeWebVariant ? 'bodyLarge' : 'subhead'}
                            color="secondary"
                            style={[styles.dueDate, isLargeWebVariant && styles.dueDateLarge]}
                        >
                            {dueDate}
                        </Typography>
                        <View style={styles.footerTrailing}>
                            {showBankPayNote && (
                                <View
                                    style={[
                                        styles.bankPayNote,
                                        { backgroundColor: colors.reservationPendingBg },
                                    ]}
                                >
                                    <Typography
                                        variant="footnote"
                                        color="primary"
                                        style={{ color: colors.reservationPendingText }}
                                    >
                                        {t('payments.payViaBankToBloom')}
                                    </Typography>
                                </View>
                            )}
                            {shouldShowStatusChip() && (
                                <StatusChip status={getStatusChipStatus()} size="small" />
                            )}
                        </View>
                    </View>
                </View>
            </TouchableOpacity>

            {/* Web-only dialog for payment details (route is not changed on web) */}
            {Platform.OS === 'web' && (
                <PaymentDetailsDialog
                    visible={isDialogVisible}
                    paymentId={id}
                    onClose={() => setIsDialogVisible(false)}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'stretch', // Changed to stretch to allow full height clickable area
        gap: 8,
    },
    containerLarge: {
        // Extra vertical spacing between rows on web
        minHeight: 72,
    },
    checkboxArea: {
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 8, // Vertical padding for clickable area
        paddingHorizontal: 4, // Small horizontal padding
        minHeight: 60, // Ensure minimum clickable height
    },
    paymentContent: {
        flex: 1,
        padding: 12,
        borderRadius: 8,
        borderWidth: 1,
        position: 'relative',
    },
    paymentContentLarge: {
        paddingVertical: 24,
        paddingHorizontal: 16,
        borderRadius: 8,
    },
    content: {
        gap: 4,
    },
    contentLarge: {
        gap: 6,
    },
    propertyName: {
        fontSize: 13,
        lineHeight: 15,
        height: 15,
    },
    propertyNameLarge: {
        fontSize: 16,
        lineHeight: 20,
        height: undefined,
    },
    paymentHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
    },
    paymentHeaderLarge: {
        gap: 40,
    },
    paymentTitle: {
        flex: 1,
        minWidth: 0,
        fontSize: 16,
        fontWeight: '600',
    },
    paymentTitleLarge: {
        fontSize: 18,
    },
    paymentAmountContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexShrink: 0,
    },
    paymentAmountContainerLarge: {
        gap: 12,
    },
    paymentAmount: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    amountText: {
        fontSize: 16,
        fontWeight: '600',
        paddingTop: 2,
    },
    amountTextLarge: {
        fontSize: 20,
        paddingTop: 0,
    },
    paymentFooter: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
    },
    footerTrailing: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexShrink: 1,
    },
    bankPayNote: {
        borderRadius: 4,
        paddingHorizontal: 8,
        paddingVertical: 4,
    },
    paymentFooterLarge: {
        marginTop: 4,
    },
    dueDate: {
        flex: 1,
        fontSize: 15,
        fontWeight: '400',
    },
    dueDateLarge: {
        fontSize: 18,
    },
});

export default PaymentItemMobile;